import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Badge,
  Button,
  Container,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CasinoIcon from '@mui/icons-material/Casino';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupIcon from '@mui/icons-material/Group';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchMyProfile } from '../../api/companions';
import { fetchChatConversations } from '../../api/chat';
import { fetchUnreadNoticeCount } from '../../api/notices';
import { loggedOut } from '../../features/auth/authSlice';
import BrandLogo from '../branding/BrandLogo';
import AccountMenu from './AccountMenu';

const navItems = [
  { labelKey: 'nav.home', path: '/dashboard', icon: HomeOutlinedIcon },
  { labelKey: 'nav.shelf', path: '/gameshelf', icon: CasinoIcon },
  { labelKey: 'nav.friends', path: '/friends', icon: GroupIcon },
  { labelKey: 'nav.chat', path: '/chat', icon: ChatBubbleOutlineIcon },
  { labelKey: 'nav.nights', path: '/nights', icon: CalendarMonthIcon },
];

const isActivePath = (pathname, path) =>
  path === '/dashboard' ? pathname === path : pathname.startsWith(path);

const PROFILE_CACHE_KEY = 'thetavern.headerProfile';
const TOKEN_STORAGE_KEY = 'tavern_token';
const MOBILE_HEADER_HEIGHT = 'calc(70px + env(safe-area-inset-top, 0px))';
const DESKTOP_HEADER_HEIGHT = '86px';
const MOBILE_NAV_CONTENT_HEIGHT = '3.75rem';
const MOBILE_NAV_FLOAT_GAP = 'max(0.875rem, env(safe-area-inset-bottom, 0px))';
const MOBILE_NAV_HEIGHT = MOBILE_NAV_CONTENT_HEIGHT;
const MOBILE_NAV_RESERVED_SPACE = `calc(${MOBILE_NAV_CONTENT_HEIGHT} + ${MOBILE_NAV_FLOAT_GAP})`;

export const appShellSurfaceStyles = {
  header: {
    position: 'fixed',
    insetInline: 0,
    top: 0,
    width: '100%',
    zIndex: 1101,
  },
  mobileNav: {
    height: MOBILE_NAV_HEIGHT,
    bottom: MOBILE_NAV_FLOAT_GAP,
    reservedSpace: MOBILE_NAV_RESERVED_SPACE,
  },
};

const getStoredAuthToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const fingerprintAuthToken = (token) => {
  let hash = 0;

  for (let index = 0; index < token.length; index += 1) {
    hash = Math.imul(31, hash) + token.charCodeAt(index);
    hash |= 0;
  }

  return `${token.length}:${hash.toString(36)}`;
};

const getStoredAuthTokenFingerprint = () => {
  const token = getStoredAuthToken();

  return token ? fingerprintAuthToken(token) : '';
};

const clearCachedProfile = () => {
  try {
    window.sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {}
};

const readCachedProfile = () => {
  try {
    const cached = window.sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    const tokenFingerprint = getStoredAuthTokenFingerprint();

    if (tokenFingerprint && parsed?.tokenFingerprint === tokenFingerprint && parsed?.profile) {
      return parsed.profile;
    }

    clearCachedProfile();
    return null;
  } catch {
    return null;
  }
};

const writeCachedProfile = (profile) => {
  try {
    const tokenFingerprint = getStoredAuthTokenFingerprint();
    if (!tokenFingerprint) return;

    window.sessionStorage.setItem(
      PROFILE_CACHE_KEY,
      JSON.stringify({ tokenFingerprint, profile })
    );
  } catch {
    // Non-critical: session storage can be unavailable in private browsing modes.
  }
};

const isAuthFailure = (error) => [401, 403].includes(Number(error?.response?.status));

const scrollToPageTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(() => readCachedProfile());
  const [profileLoading, setProfileLoading] = useState(!profile);
  const [unreadNotices, setUnreadNotices] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const headerUser = profile
    ? {
        name: profile.displayName,
        title: profile.tavernTitle,
        avatar: profile.avatarUrl || '',
        role: profile.role || 'patron',
      }
    : null;

  useEffect(() => {
    let mounted = true;

    fetchMyProfile()
      .then((data) => {
        if (!mounted) return;
        setProfile(data);
        writeCachedProfile(data);
      })
      .catch((error) => {
        if (!mounted || !isAuthFailure(error)) return;

        clearCachedProfile();
        setProfile(null);
        dispatch(loggedOut());
        navigate('/login', { replace: true });
      })
      .finally(() => {
        if (mounted) setProfileLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [dispatch, location.pathname, navigate]);

  useEffect(() => {
    const updateHeaderSurface = () => {
      setHeaderScrolled(window.scrollY > 10);
    };

    updateHeaderSurface();
    window.addEventListener('scroll', updateHeaderSurface, { passive: true });

    return () => window.removeEventListener('scroll', updateHeaderSurface);
  }, []);

  useEffect(() => {
    let mounted = true;

    const refreshUnreadChats = () => {
      fetchChatConversations()
        .then((data) => {
          if (!mounted) return;
          const count = (data.items || []).reduce(
            (sum, conversation) => sum + Number(conversation.unreadCount || 0),
            0
          );
          setUnreadChats(count);
        })
        .catch(() => {});
    };

    const handleServerEvent = (event) => {
      const type = event.detail?.type || '';
      if (type.startsWith('chat.')) refreshUnreadChats();
    };

    const handleFocus = () => refreshUnreadChats();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadChats();
      }
    };

    refreshUnreadChats();
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('thetavern:revalidate', refreshUnreadChats);
    window.addEventListener('thetavern:chat-revalidate', refreshUnreadChats);
    window.addEventListener('thetavern:server-event', handleServerEvent);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('thetavern:revalidate', refreshUnreadChats);
      window.removeEventListener('thetavern:chat-revalidate', refreshUnreadChats);
      window.removeEventListener('thetavern:server-event', handleServerEvent);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const refreshUnread = () => {
      fetchUnreadNoticeCount()
        .then((data) => {
          if (mounted) setUnreadNotices(Number(data.count || 0));
        })
        .catch(() => {});
    };

    const handleServerEvent = (event) => {
      const type = event.detail?.type || '';
      if (['notice.created', 'notice.updated', 'notice.read', 'notice.archived'].includes(type)) {
        refreshUnread();
      }
    };

    const handleFocus = () => refreshUnread();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnread();
      }
    };

    refreshUnread();
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('thetavern:revalidate', refreshUnread);
    window.addEventListener('thetavern:server-event', handleServerEvent);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('thetavern:revalidate', refreshUnread);
      window.removeEventListener('thetavern:server-event', handleServerEvent);
    };
  }, []);

  const handleLogout = () => {
    clearCachedProfile();
    dispatch(loggedOut());
    navigate('/login', { replace: true });
  };

  const handlePrimaryNavigationClick = (event, path) => {
    if (!isActivePath(location.pathname, path)) return;

    event.preventDefault();
    scrollToPageTop();
  };

  return (
    <Box
      sx={{
        '--tavern-mobile-nav-reserved-space': appShellSurfaceStyles.mobileNav.reservedSpace,
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': {
          minHeight: '100dvh',
        },
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: {
          xs: MOBILE_HEADER_HEIGHT,
          md: DESKTOP_HEADER_HEIGHT,
        },
        pb: {
          xs: `calc(${appShellSurfaceStyles.mobileNav.reservedSpace} + 1.25rem)`,
          md: 5,
        },
        background:
          'radial-gradient(circle at 12% 10%, rgba(215,160,75,0.22), transparent 26rem), radial-gradient(circle at 88% 72%, rgba(15,76,69,0.14), transparent 32rem)',
      }}
    >
      <Box
        component="header"
        sx={{
          ...appShellSurfaceStyles.header,
          bgcolor: {
            xs: headerScrolled ? 'rgba(245, 241, 230, 0.9)' : 'rgba(245, 241, 230, 0.78)',
            md: headerScrolled ? 'rgba(245, 241, 230, 0.9)' : 'rgba(245, 241, 230, 0.78)',
          },
          borderBottom: '1px solid',
          borderColor: {
            xs: headerScrolled ? 'rgba(15, 76, 69, 0.16)' : 'rgba(15, 76, 69, 0.08)',
            md: headerScrolled ? 'rgba(15, 76, 69, 0.16)' : 'rgba(15, 76, 69, 0.08)',
          },
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: {
            xs: headerScrolled ? '0 14px 30px -28px rgba(6, 37, 34, 0.58)' : 'none',
            md: headerScrolled ? '0 14px 30px -28px rgba(6, 37, 34, 0.58)' : 'none',
          },
          transition:
            'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, backdrop-filter 180ms ease',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            pt: {
              xs: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
              md: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            },
            pb: { xs: 1, md: 1.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 0.75, sm: 2 },
            minWidth: 0,
          }}
        >
          <Stack
            component={RouterLink}
            to="/dashboard"
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              minWidth: 0,
              flex: { xs: '1 1 auto', md: '0 1 240px', lg: '0 1 280px' },
              overflow: 'hidden',
            }}
          >
            <BrandLogo emblemSize={46} />
          </Stack>

          <Paper
            component="nav"
            variant="outlined"
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              p: 0.5,
              borderRadius: 999,
              bgcolor: headerScrolled ? 'rgba(255, 250, 240, 0.72)' : 'rgba(255, 250, 240, 0.54)',
              borderColor: 'rgba(15, 76, 69, 0.16)',
              backdropFilter: 'blur(16px)',
              flexShrink: 1,
              minWidth: 0,
              transition: 'background-color 180ms ease',
            }}
          >
            {navItems.map(({ icon: Icon, labelKey, path }) => {
              const active = isActivePath(location.pathname, path);
              const label = t(labelKey);
              const badgeContent = path === '/chat' ? unreadChats : 0;

              return (
                <Button
                  key={path}
                  component={RouterLink}
                  to={path}
                  color={active ? 'primary' : 'inherit'}
                  onClick={(event) => handlePrimaryNavigationClick(event, path)}
                  startIcon={
                    <Badge
                      badgeContent={badgeContent}
                      color="secondary"
                      max={9}
                      invisible={badgeContent <= 0}
                    >
                      <Icon />
                    </Badge>
                  }
                  variant={active ? 'contained' : 'text'}
                  sx={{ px: { md: 1.35, lg: 2.25 } }}
                >
                  {label}
                </Button>
              );
            })}
          </Paper>

          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 1.5 }}
            alignItems="center"
            sx={{ flexShrink: 0, minWidth: 0 }}
          >
            <Tooltip title={t('layout.openNotices')}>
              <IconButton
                aria-label={t('layout.openNotices')}
                onClick={() => navigate('/notices')}
                sx={{
                  display: { xs: 'inline-flex', md: 'inline-flex' },
                  color: location.pathname.startsWith('/notices') ? 'secondary.main' : 'text.secondary',
                  bgcolor: location.pathname.startsWith('/notices')
                    ? 'rgba(236, 170, 65, 0.12)'
                    : 'transparent',
                }}
              >
                <Badge
                  badgeContent={unreadNotices}
                  color="secondary"
                  max={9}
                  invisible={unreadNotices <= 0}
                >
                  <NotificationsNoneIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              {headerUser ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('layout.welcomeBack')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Cinzel", Georgia, serif',
                      fontSize: '0.9rem',
                      lineHeight: 1.1,
                    }}
                  >
                    {headerUser.title} {headerUser.name}
                  </Typography>
                </>
              ) : profileLoading ? (
                <Stack spacing={0.5} alignItems="flex-end">
                  <Skeleton variant="text" width={88} />
                  <Skeleton variant="text" width={128} />
                </Stack>
              ) : null}
            </Box>
            <AccountMenu user={headerUser} onLogout={handleLogout} />
          </Stack>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        component="main"
        sx={{
          flex: '1 0 auto',
          width: '100%',
          pt: {
            xs: 1.5,
            md: 1.5,
          },
          scrollPaddingTop: {
            xs: MOBILE_HEADER_HEIGHT,
            md: 0,
          },
        }}
      >
        {children}
      </Container>

      <Container
        maxWidth="lg"
        component="footer"
        sx={{
          mt: 'auto',
          pt: { xs: 4, md: 6 },
          pb: { xs: 3, md: 0 },
        }}
      >
        <Stack
          component="nav"
          direction="row"
          spacing={1.75}
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
          textAlign="center"
        >
          <Link component={RouterLink} to="/privacy" variant="body2" color="text.secondary">
            {t('legalLinks.privacy')}
          </Link>
          <Link component={RouterLink} to="/terms" variant="body2" color="text.secondary">
            {t('legalLinks.terms')}
          </Link>
          <Link component={RouterLink} to="/legal" variant="body2" color="text.secondary">
            {t('legalLinks.legal')}
          </Link>
          <Link component={RouterLink} to="/privacy/contact" variant="body2" color="text.secondary">
            {t('legalLinks.requests')}
          </Link>
        </Stack>
      </Container>

      <Paper
        component="nav"
        aria-label={t('layout.primaryNavigation')}
        sx={{
          position: 'fixed',
          left: '50%',
          right: 'auto',
          bottom: appShellSurfaceStyles.mobileNav.bottom,
          width: 'auto',
          maxWidth: 'calc(100% - 3rem)',
          height: appShellSurfaceStyles.mobileNav.height,
          boxSizing: 'border-box',
          zIndex: (theme) => theme.zIndex.appBar,
          display: { xs: 'block', md: 'none' },
          border: '1px solid',
          borderColor: 'rgba(15, 76, 69, 0.22)',
          borderRadius: 999,
          bgcolor: 'rgba(255, 250, 240, 0.94)',
          color: 'primary.dark',
          background:
            'linear-gradient(135deg, rgba(255, 250, 240, 0.96), rgba(238, 221, 179, 0.94))',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          transform: 'translate3d(-50%, 0, 0)',
          p: 0.55,
          boxShadow:
            '0 18px 38px -24px rgba(6, 37, 34, 0.72), inset 0 1px 0 rgba(255,255,255,0.55)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ height: '100%', width: 'max-content', maxWidth: '100%' }}
        >
          {navItems.map(({ icon: Icon, labelKey, path }) => {
            const active = isActivePath(location.pathname, path);
            const label = t(labelKey);
            const badgeContent = path === '/chat' ? unreadChats : 0;

            return (
              <Button
                key={path}
                component={RouterLink}
                to={path}
                aria-label={label}
                onClick={(event) => handlePrimaryNavigationClick(event, path)}
                sx={{
                  flex: '0 0 auto',
                  minWidth: 0,
                  width: 46,
                  height: 42,
                  p: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 999,
                  color: active ? '#fff8e8' : 'rgba(15, 76, 69, 0.72)',
                  bgcolor: active ? 'primary.dark' : 'transparent',
                  '&:hover': {
                    bgcolor: active ? 'primary.main' : 'rgba(15, 76, 69, 0.08)',
                  },
                }}
              >
                <Badge
                  badgeContent={badgeContent}
                  color="secondary"
                  max={9}
                  invisible={badgeContent <= 0}
                >
                  <Icon sx={{ fontSize: 25 }} />
                </Badge>
              </Button>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
};

export default AppLayout;
